'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface Seller {
  name: string;
  niche: string;
  gmv: string;
  followers: string;
  rating: number;
  reviews: number;
  tags: string[];
  available: boolean;
  img: number;
  accent: string;
}

const sellers: Seller[] = [
  {
    name: 'Aria Chen',
    niche: 'Beauty & Skincare',
    gmv: '$320K/mo',
    followers: '2.4M',
    rating: 5.0,
    reviews: 142,
    tags: ['Skincare', 'Makeup', 'K-Beauty'],
    available: true,
    img: 30,
    accent: 'bg-theme-01',
  },
  {
    name: 'Marcus Thompson',
    niche: 'Fashion & Streetwear',
    gmv: '$480K/mo',
    followers: '3.8M',
    rating: 4.9,
    reviews: 218,
    tags: ['Streetwear', 'Sneakers', 'Luxury'],
    available: true,
    img: 20,
    accent: 'bg-theme-04',
  },
  {
    name: 'Priya Sharma',
    niche: 'Health & Wellness',
    gmv: '$195K/mo',
    followers: '1.6M',
    rating: 4.8,
    reviews: 97,
    tags: ['Supplements', 'Yoga', 'Nutrition'],
    available: false,
    img: 25,
    accent: 'bg-theme-03',
  },
  {
    name: 'Jake Rivera',
    niche: 'Tech & Gadgets',
    gmv: '$560K/mo',
    followers: '4.2M',
    rating: 4.9,
    reviews: 305,
    tags: ['Electronics', 'Gaming', 'Smart Home'],
    available: true,
    img: 33,
    accent: 'bg-theme-02',
  },
  {
    name: 'Sofia Laurent',
    niche: 'Home & Living',
    gmv: '$140K/mo',
    followers: '980K',
    rating: 4.7,
    reviews: 63,
    tags: ['Decor', 'Kitchen', 'Organization'],
    available: true,
    img: 40,
    accent: 'bg-theme-04',
  },
  {
    name: 'David Kim',
    niche: 'Food & Beverage',
    gmv: '$280K/mo',
    followers: '2.1M',
    rating: 5.0,
    reviews: 189,
    tags: ['Snacks', 'Drinks', 'Asian Food'],
    available: true,
    img: 45,
    accent: 'bg-theme-01',
  },
];

const niches = ['All', 'Beauty', 'Fashion', 'Health', 'Tech', 'Home', 'Food'];

export default function SellersSection() {
  const [activeNiche, setActiveNiche] = useState('All');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.seller-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const filteredSellers = activeNiche === 'All'
    ? sellers
    : sellers.filter(s => s.niche.toLowerCase().includes(activeNiche.toLowerCase()));

  return (
    <section id="for-sellers" className="py-24 bg-neutral-01" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Title */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="mb-5">
              <div
                className="bg-theme-02 text-dark text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
                style={{ transform: 'rotate(-6deg)' }}
              >
                Top Sellers
              </div>
            </div>
            <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-3">
              Meet our top performers
            </h2>
            <p className="text-[17px] text-dark/60 max-w-[500px] leading-relaxed">
              Verified TikTok Shop sellers with proven GMV records, ready to represent your brand.
            </p>
          </div>
          <Link
            href="/sellers"
            className="flex-shrink-0 font-bold text-[15px] text-dark border border-dark/30 px-7 py-3.5 rounded-pill hover:bg-dark hover:text-white transition-all duration-200"
          >
            Browse All Sellers
          </Link>
        </div>

        {/* Niche filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {niches.map((niche) => (
            <button
              key={niche}
              onClick={() => setActiveNiche(niche)}
              className={`font-semibold text-[14px] px-5 py-2 rounded-full transition-all duration-200 ${
                activeNiche === niche
                  ? 'bg-dark text-white' :'bg-white text-dark hover:bg-neutral-02'
              }`}
            >
              {niche}
            </button>
          ))}
        </div>

        {/* Seller cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredSellers.map((seller, i) => (
            <div
              key={seller.name}
              className="seller-card bg-white rounded-[24px] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer group"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <img
                      src={`https://i.pravatar.cc/56?img=${seller.img}`}
                      alt={seller.name}
                      className="w-14 h-14 rounded-2xl object-cover"
                    />
                    {seller.available && (
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-theme-green rounded-full border-2 border-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-dark text-[16px]">{seller.name}</p>
                    <p className="text-dark/50 text-[13px]">{seller.niche}</p>
                  </div>
                </div>
                <div className={`w-8 h-8 ${seller.accent} rounded-xl flex items-center justify-center`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" fill="#120A0B"/>
                  </svg>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 mb-5">
                <div className="bg-neutral-01 rounded-xl p-2.5 text-center">
                  <p className="text-[11px] text-dark/50 mb-0.5">GMV/mo</p>
                  <p className="text-[13px] font-bold text-dark">{seller.gmv}</p>
                </div>
                <div className="bg-neutral-01 rounded-xl p-2.5 text-center">
                  <p className="text-[11px] text-dark/50 mb-0.5">Followers</p>
                  <p className="text-[13px] font-bold text-dark">{seller.followers}</p>
                </div>
                <div className="bg-neutral-01 rounded-xl p-2.5 text-center">
                  <p className="text-[11px] text-dark/50 mb-0.5">Rating</p>
                  <p className="text-[13px] font-bold text-dark">⭐ {seller.rating}</p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5 mb-5">
                {seller.tags.map((tag) => (
                  <span key={tag} className="text-[11px] font-medium text-dark/60 bg-neutral-01 px-3 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-neutral-02">
                <span className={`text-[12px] font-semibold px-3 py-1 rounded-full ${
                  seller.available ? 'bg-theme-green/15 text-theme-green' : 'bg-neutral-02 text-dark/40'
                }`}>
                  {seller.available ? 'Available' : 'Booked'}
                </span>
                <Link href={`/seller/${seller.name.replace(' ', '-')}`} className="font-bold text-[13px] text-dark group-hover:text-theme-01 transition-colors duration-200 flex items-center gap-1">
                  View Profile
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
